import React, { useEffect, useImperativeHandle, useMemo } from 'react';

import { Box } from '@mui/system';
import { useFormik } from 'formik';

import {
  calendarReducerInitialState,
  pharmacistFilterValues,
  updateFilter,
} from '@pharmaplan/common';

import FilterGenerator from '../../../common/FilterGenerator';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import useGetItems from '../../../../hooks/useGetItems';
import { dashboardFilter } from '../../../../helpers/Filters';

import classes from './styles';

interface IDashboardFilter {
  isMap:boolean;
  setFilterState: (
    value: React.SetStateAction<{
      submitted: boolean;
      showFilter: boolean;
    }>
  ) => void;
  filterState: {
    submitted: boolean;
    showFilter: boolean;
  };
}

const DashboardFilter = React.forwardRef(
  ({ filterState, setFilterState, isMap }: IDashboardFilter, ref) => {
    const dispatch = useAppDispatch();

    const filterValues = useAppSelector(pharmacistFilterValues);
    const sortedSoftwares = useMemo(
      () =>
        filterValues.softwares.toSorted((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
      [filterValues],
    );

    const { getPharmacyName } = useGetItems();

    const bannerName = getPharmacyName(filterValues.banners);

    const formik = useFormik({
      initialValues: {
        ...calendarReducerInitialState?.filter,
        weekVolume: null,
        weekendVolume: null,
      },
      enableReinitialize: true,
      onSubmit: (values) => {
        setFilterState({ ...filterState, submitted: true });
        dispatch(updateFilter({ ...values }));
      },
    });

    const resetFilter = () => {
      dispatch(
        updateFilter({
          ...calendarReducerInitialState?.filter,
          weekVolume: null,
          weekendVolume: null,
        }),
      );
      setFilterState({ ...filterState, submitted: false });
      formik.resetForm();
    };

    useEffect(
      () =>
        () => {
          dispatch(
            updateFilter({
              ...calendarReducerInitialState?.filter,
              weekVolume: null,
              weekendVolume: null,
            }),
          );
        // eslint-disable-next-line react-hooks/exhaustive-deps
        },
      [],
    );

    useImperativeHandle(ref, () =>
      ({
        reset() {
          resetFilter();
        },
        clear() {
          formik.resetForm();
        },
      }));

    return (
      <Box component="div" sx={classes.container}>
        {filterState.showFilter && (
          <FilterGenerator
            isDashboard
            filter={dashboardFilter(filterValues, resetFilter, bannerName, sortedSoftwares, isMap)}
            formik={formik}
          />
        )}
      </Box>
    );
  },
);

export default DashboardFilter;
